import { useState } from 'react';
import { Link } from 'react-router-dom';

const reasons = [
  {
    icon: 'ri-map-pin-line',
    title: 'Serving the Entire Oregon Coast',
    body: 'From Astoria to Tillamook, Seaside to Cannon Beach — we deliver vehicles right to your door. No need to drive hours to a big-city dealership.',
    link: '/service-areas',
    linkLabel: 'See all service areas',
  },
  {
    icon: 'ri-coin-line',
    title: 'Flat $250 Doc Fee. Period.',
    body: 'Most dealerships bury hundreds in junk fees. We charge a flat $250 doc fee — no add-ons, no surprises, no negotiating required.',
    link: '/how-it-works',
    linkLabel: 'How our pricing works',
  },
  {
    icon: 'ri-user-star-line',
    title: 'A Broker on Your Side',
    body: 'We work for you, not the bank. Our broker model means we find the right car at the right price — not just whatever\'s on our lot.',
    link: '/broker-services',
    linkLabel: 'Learn about broker services',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'No-Pressure. Guaranteed.',
    body: 'No commissioned salespeople, no back-room tactics, no surprise fees at signing. Just transparent pricing and straight talk — from first call to final handshake.',
    link: '/rockhound-difference',
    linkLabel: 'The Rockhound difference',
  },
  {
    icon: 'ri-bank-card-line',
    title: 'Financing for All Credit Situations',
    body: 'First-time buyer, rebuilding credit, or just want a better rate? We work with lenders across Oregon and Washington to find options that fit.',
    link: '/financing',
    linkLabel: 'Explore financing options',
  },
  {
    icon: 'ri-footprint-line',
    title: '$100 Per Sale to Oregon Dog Rescue',
    body: 'Every vehicle we sell puts $100 toward Oregon dog rescue organizations. Buy a car, save a pup. It\'s that simple.',
    link: '/community-impact',
    linkLabel: 'Our community commitment',
  },
];

const quickFacts = [
  {
    icon: 'ri-file-text-line',
    stat: '$250',
    label: 'Flat Doc Fee',
    sub: 'Oregon avg. is $500–$1,200',
  },
  {
    icon: 'ri-truck-line',
    stat: '50 mi',
    label: 'Free Delivery',
    sub: 'We bring it to your door',
  },
  {
    icon: 'ri-shield-check-line',
    stat: '$0',
    label: 'Hidden Fees',
    sub: 'Price you see is what you pay',
  },
  {
    icon: 'ri-checkbox-circle-line',
    stat: '100%',
    label: 'Inspected Inventory',
    sub: 'Every car, every time',
  },
];

export default function OregonCoastWhyUs() {
  const [showAll, setShowAll] = useState(false);

  return (
    <section
      aria-labelledby="why-us-heading"
      className="bg-white py-10 md:py-20 px-4"
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8 md:mb-14">
          <span className="inline-flex items-center gap-2 bg-[#E8B84B]/10 text-[#92650a] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3">
            <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
              <i className="ri-trophy-line"></i>
            </span>
            Why Oregon Coast Buyers Choose Rockhound
          </span>
          <h2
            id="why-us-heading"
            className="text-2xl sm:text-4xl font-bold text-[#1A2332] mb-3"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            A Different Kind of Car Dealer
          </h2>
          <p className="text-[#4A5568] max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Oregon Coast families deserve a dealership that's actually on their side. Here's what makes Rockhound Auto different.
          </p>
        </div>

        {/* Grid — 3 cards on mobile by default, all 6 on md+ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {reasons.map((reason, i) => {
            const hiddenOnMobile = i >= 3 && !showAll;
            return (
              <article
                key={reason.title}
                className={`group bg-[#F7F5F0] rounded-xl p-5 border border-[#E8E2D6] hover:border-[#E8B84B]/60 hover:shadow-md transition-all duration-300 ${hiddenOnMobile ? 'hidden md:block' : ''}`}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8B84B]/15 rounded-lg mb-3 group-hover:bg-[#E8B84B]/25 transition-colors" aria-hidden="true">
                  <i className={reason.icon} style={{ fontSize: '1.25rem', color: '#E8B84B' }}></i>
                </div>
                <h3 className="text-[#1A2332] font-bold text-sm mb-1.5" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {reason.title}
                </h3>
                <p className="text-[#4A5568] text-xs leading-relaxed mb-3">
                  {reason.body}
                </p>
                <Link
                  to={reason.link}
                  className="inline-flex items-center gap-1.5 text-[#92650a] text-xs font-semibold hover:gap-2.5 transition-all duration-200 cursor-pointer"
                >
                  {reason.linkLabel}
                  <span aria-hidden="true">→</span>
                </Link>
              </article>
            );
          })}
        </div>

        {/* Show more button — mobile only */}
        {!showAll && (
          <div className="flex justify-center mb-6 md:hidden">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 bg-[#F7F5F0] border border-[#E8E2D6] hover:border-[#E8B84B] text-[#1A2332] px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer"
            >
              <span aria-hidden="true">＋</span>
              Show 3 More Reasons
            </button>
          </div>
        )}

        {/* Quick Facts Bar — 2x2 on mobile, 4-col on md+ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {quickFacts.map((fact) => (
            <div
              key={fact.label}
              className="bg-[#F7F5F0] rounded-xl p-4 text-center border border-[#E8E2D6] hover:border-[#E8B84B] hover:shadow-md transition-all duration-300"
            >
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2 bg-white rounded-full border border-[#E8E2D6]" aria-hidden="true">
                <i className={fact.icon} style={{ fontSize: '1rem', color: '#E8B84B' }}></i>
              </div>
              <div className="text-xl font-bold text-[#1A2332]" style={{ fontFamily: 'Oswald, sans-serif' }}>{fact.stat}</div>
              <div className="text-xs font-semibold text-[#1A2332] mb-0.5">{fact.label}</div>
              <div className="text-xs text-[#4B5563] hidden sm:block">{fact.sub}</div>
            </div>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="bg-[#1A2332] rounded-2xl px-5 py-6 md:px-8 md:py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-white font-bold text-base md:text-lg mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Ready to find your next vehicle?
            </p>
            <p className="text-white/60 text-xs md:text-sm hidden sm:block">
              Browse our current inventory or let our broker team find exactly what you're looking for.
            </p>
          </div>
          <div className="flex flex-row gap-2 flex-shrink-0 w-full sm:w-auto">
            <Link
              to="/inventory"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#E8B84B] hover:bg-[#d4a43a] text-[#1A2332] px-5 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                <i className="ri-car-line"></i>
              </span>
              Browse Inventory
            </Link>
            <Link
              to="/broker-services/request-a-vehicle"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer border border-white/20"
            >
              <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                <i className="ri-search-line"></i>
              </span>
              Request a Vehicle
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}